import videojs from 'video.js';

import { PauseAdMode } from '@src/plugins/Controller';
import { Event } from '@src/types/event';
import type { UserPlayerOptions } from '@src/types/player';
import { getIsBrowser } from '@src/utils/device';

import PlayerWrapper, { IPlayerProxy } from './PlayerWrapper';

export type { IPlayerProxy };

class PlayerManager {
  private _players: Map<string, IPlayerProxy> = new Map();

  async createPlayerAsync(
    containerElementOrId: string | HTMLVideoElement | HTMLDivElement,
    options?: UserPlayerOptions,
    ready?: videojs.ReadyCallback
  ) {
    const { proxy } =
      (await PlayerWrapper.from(containerElementOrId, options, ready, this)) ??
      {};

    if (!proxy) return;

    this.setupVisibilityHandler();
    this.setupInteractivities(proxy);

    this._players.set(proxy.id(), proxy);

    return proxy;
  }

  setupVisibilityHandler() {
    if (!getIsBrowser()) return;
    document.addEventListener(
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
      // Safari support on iOS (14.5) and macOS (14.1) in 2021
      'visibilitychange',
      () => {
        // pause all player when the page is not active
        if (document.hidden) {
          this._players.forEach((proxy) => {
            const overrideNative = proxy.options_.html5?.vhs?.overrideNative;

            const pauseVideoMode =
              videojs.browser.IS_IPHONE ||
              videojs.browser.IS_ANDROID ||
              !overrideNative;

            proxy.pauseVideoOrAd({
              pauseAdMode: false,
              pauseVideoMode,
              isForcePauseVideo: pauseVideoMode,
            });
          });
        }
      },
      false
    );
  }

  setupInteractivities(proxy: IPlayerProxy) {
    proxy.on([Event.PLAY, Event.PLAYING], () => {
      this._players.forEach((otherProxy) => {
        if (
          otherProxy === proxy ||
          otherProxy?.isDisposed?.() ||
          proxy?.paused?.()
        )
          return;

        otherProxy.pauseVideoOrAd();
      });
    });

    proxy.one(Event.DISPOSE, () => {
      this.removePlayer(proxy.id());
    });
  }

  getPlayers() {
    return this._players;
  }

  getPlayer(id: string) {
    return this._players.get(id);
  }

  removePlayer(id: string) {
    this._players.delete(id);
  }
}

const defaultPlayerManager = getIsBrowser() ? new PlayerManager() : null;

export { defaultPlayerManager as playerManager };

export default PlayerManager;
